import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { applyTheme, readTheme } from "@/components/ThemeToggle";

applyTheme(readTheme());

// Sprint 60.36 — Frontend Sentry. Silent no-op when DSN is unset (dev/local
// runs stay quiet). release + environment mirror the backend tagging so
// Seer can pin frontend crashes to a specific deploy. `browserTracing`
// captures React-render/route perf spans; `replay` records 10% of sessions
// (bumped to 100% when an error fires) so Seer sees the exact clicks that
// led up to the crash.
const _SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (_SENTRY_DSN) {
  Sentry.init({
    dsn: _SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV || "preview",
    release: process.env.REACT_APP_SENTRY_RELEASE || "warpaint-dfs-frontend",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // React 19 StrictMode double-invokes effects in dev — dedupe those
    // extra events so we don't burn our Sentry quota on non-bugs.
    beforeSend(event) {
      if (event.request?.url?.includes("localhost")) return null;
      return event;
    },
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{
          minHeight: "100vh", background: "#0a0a0a", color: "#fafafa",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "-apple-system, sans-serif", padding: "24px",
        }}>
          <div style={{ maxWidth: 480, textAlign: "left" }}>
            <div style={{
              color: "#39FF14", fontSize: 11, letterSpacing: "0.15em",
              textTransform: "uppercase", marginBottom: 12,
            }}>
              WarPaint DFS · Fatal Error
            </div>
            <h1 style={{ fontSize: 22, marginBottom: 12 }}>
              Something crashed on our end.
            </h1>
            <p style={{ color: "#a1a1aa", fontSize: 14, marginBottom: 20 }}>
              We captured the error and our team's been paged. Try refreshing —
              if it keeps happening, email <a href="mailto:support@warpaintdfs.com"
              style={{ color: "#39FF14" }}>support@warpaintdfs.com</a>.
            </p>
            <button
              onClick={resetError}
              style={{
                background: "#39FF14", color: "#000", border: 0,
                padding: "12px 20px", fontSize: 12, fontWeight: 700,
                letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer",
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
